import { useState } from 'react';

import {
  Edit as EditIcon,
  Key as KeyIcon,
  PersonAdd as PersonAddIcon,
  SwapHoriz as SwapIcon,
} from '@mui/icons-material';
import { Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';

import { BgtUserFormDialog } from '@/components/residents/BgtUserFormDialog';
import { CredentialsDialog } from '@/components/residents/CredentialsDialog';
import {
  AccountSwapDialog,
  type AccountKind,
  type SwapTarget,
} from '@/components/residents/AccountSwapDialog';
import {
  type IssuedCredentials,
  getResidentCredentials,
  issueVoiceAccount,
  resetVoicePassword,
} from '@/lib/api/credentials';
import type { ResidentDto } from '@/lib/api/residents';
import { useHasRole } from '@/lib/hooks/useHasRole';

interface Props {
  resident: ResidentDto;
}

function AccountRow({
  label,
  linked,
  detail,
  canEdit,
  onEdit,
}: {
  label: string;
  linked: boolean;
  detail: string;
  canEdit: boolean;
  onEdit: () => void;
}) {
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography variant="caption" color="text.secondary" sx={{ width: 116, flexShrink: 0 }}>
        {label}
      </Typography>
      {linked ? (
        <Typography variant="body2" sx={{ minWidth: 0 }} noWrap>
          {detail}
        </Typography>
      ) : (
        <Chip size="small" label="미연결" variant="outlined" color="warning" />
      )}
      <Box sx={{ flex: 1 }} />
      {canEdit && (
        <Tooltip title={`${label} 변경`}>
          <IconButton
            size="small"
            aria-label={`${label} 변경`}
            onClick={onEdit}
            sx={{ color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
          >
            <SwapIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof AxiosError) {
    return (err.response?.data as { message?: string } | undefined)?.message ?? fallback;
  }
  return fallback;
}

/**
 * 상세 패널의 연결 계정 영역.
 *
 * 계정 교체는 자주 하는 조작이 아닌데 목록 셀에 버튼을 두면 매 행마다 노출되어
 * 표가 복잡해지고 오조작 여지도 생긴다. 대상자를 열어 확인한 뒤 바꾸는 흐름이
 * 자연스러워 상세 패널로 옮겼다.
 *
 * 자격증명 보기도 여기 둔다. 직원이 환자에게 불러줄 값(환자번호·생년월일·아이디)을
 * 한 화면에 모아 보여주는 것이 목적이다.
 */
export function LinkedAccounts({ resident }: Props) {
  const canEdit = useHasRole('ADMIN', 'MANAGER');
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [kind, setKind] = useState<AccountKind | null>(null);
  const [viewing, setViewing] = useState(false);
  const [editingBgtUser, setEditingBgtUser] = useState(false);
  /** 방금 발급받아 딱 한 번 보여줄 값. 창을 닫으면 버린다 */
  const [issued, setIssued] = useState<IssuedCredentials | null>(null);

  const credentialsQuery = useQuery({
    queryKey: ['residents', 'credentials', resident.id],
    queryFn: () => getResidentCredentials(resident.id),
    enabled: viewing,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['residents'] });
  };

  const issueMutation = useMutation({
    mutationFn: () => issueVoiceAccount(resident.id),
    onSuccess: (data) => {
      setIssued(data);
      invalidate();
    },
    onError: (err) =>
      enqueueSnackbar(errorMessage(err, '계정 발급에 실패했습니다'), { variant: 'error' }),
  });

  const resetMutation = useMutation({
    mutationFn: () => resetVoicePassword(resident.id),
    onSuccess: (data) => {
      setViewing(false);
      setIssued(data);
      invalidate();
    },
    onError: (err) =>
      enqueueSnackbar(errorMessage(err, '비밀번호 재발급에 실패했습니다'), { variant: 'error' }),
  });

  const target: SwapTarget = {
    id: resident.id,
    roomNumber: resident.room?.roomNumber ?? null,
    name: resident.user?.name ?? null,
    userId: resident.userId,
    voiceUserId: resident.voiceUserId,
  };

  const bgtDetail = resident.user
    ? [resident.user.name, resident.user.patientCode].filter(Boolean).join(' · ') || '(미지정)'
    : '(미지정)';

  return (
    <>
      <Stack spacing={0.5}>
        <AccountRow
          label="브레인가드테라피"
          linked={resident.userId !== null}
          detail={bgtDetail}
          canEdit={canEdit}
          onEdit={() => setKind('bgt')}
        />
        <AccountRow
          label="보이스웰니스"
          linked={resident.voiceUserId !== null}
          detail="연결됨"
          canEdit={canEdit}
          onEdit={() => setKind('voice')}
        />

        {canEdit && (
          <Stack direction="row" spacing={1} sx={{ pt: 0.5 }}>
            <Button size="small" startIcon={<KeyIcon />} onClick={() => setViewing(true)}>
              자격증명 보기
            </Button>
            {resident.userId !== null && (
              <Button size="small" startIcon={<EditIcon />} onClick={() => setEditingBgtUser(true)}>
                BGT 정보 수정
              </Button>
            )}
            {resident.voiceUserId === null && (
              <Button
                size="small"
                startIcon={<PersonAddIcon />}
                onClick={() => issueMutation.mutate()}
                disabled={issueMutation.isPending}
              >
                {issueMutation.isPending ? '발급 중…' : '보이스웰니스 계정 발급'}
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      <AccountSwapDialog
        open={kind !== null}
        kind={kind ?? 'voice'}
        resident={target}
        onClose={() => setKind(null)}
      />

      {/*
        오타로 만들어진 계정을 고치는 유일한 경로. 생성 수단만 열고 이걸 빼면
        잘못 입력된 환자번호·생년월일이 영구히 남고 그 대상자는 앱에 로그인하지 못한다.
      */}
      <BgtUserFormDialog
        open={editingBgtUser}
        initial={
          resident.userId
            ? {
                id: resident.userId,
                patientCode: resident.user?.patientCode ?? null,
                birthDate: resident.user?.birthDate ?? null,
                name: resident.user?.name ?? null,
              }
            : null
        }
        onClose={() => setEditingBgtUser(false)}
        onSaved={() => {
          invalidate();
          void queryClient.invalidateQueries({ queryKey: ['residents', 'credentials'] });
        }}
      />

      {/* 발급 직후 1회 — 비밀번호가 보이는 유일한 화면 */}
      <CredentialsDialog
        open={issued !== null}
        title={`${resident.room?.roomNumber ?? ''}호 계정이 발급되었습니다`.trim()}
        credentials={issued}
        onClose={() => setIssued(null)}
      />

      {/* 이후 다시 보기 — 비밀번호 자리는 블러 처리된다 */}
      <CredentialsDialog
        open={viewing}
        title="로그인 정보"
        credentials={credentialsQuery.data ?? null}
        onResetPassword={() => resetMutation.mutate()}
        resetting={resetMutation.isPending}
        onClose={() => setViewing(false)}
      />
    </>
  );
}
